// Page Layout
.page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-background-primary);
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 map.get($spacing-scale, 'xl');
  width: 100%;
}

.wrapper {
  width: 100%;
  max-width: 1200px;
  padding: map.get($spacing-scale, 'lg') 0;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (min-width: $breakpoint-md) {
    height: calc(100dvh - 100px);
    flex: none;
  }
}

// Card (for empty state)
.card {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'lg');
  overflow: hidden;
}

// Two-column content
.content {
  flex: 1;
  display: flex;
  background: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'lg');
  overflow: hidden;
}

// Type List (Left Sidebar)
.typeList {
  width: 340px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--color-border);
  background: var(--color-surface-primary);
}

.filterRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: map.get($spacing-scale, 'sm') map.get($spacing-scale, 'md');
  border-bottom: 1px solid var(--color-border);
  min-height: 52px;
}

.countLabel {
  @include metadata-medium;
  color: var(--color-text-secondary);
}

.countFiltered {
  color: var(--color-primary-base);
}

.filterActive {
  color: var(--color-primary-base);
}

.typeListHeader {
  display: flex;
  align-items: stretch;
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
}

.typeListHeaderName {
  flex: 1;
}

.typeListHeaderOutput {
  width: 100px;

  // Right-align text in TableHeaderCell
  span {
    text-align: right;
  }
}

.typeListItems {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.noResultsState {
  padding: map.get($spacing-scale, 'xl') map.get($spacing-scale, 'md');
  display: flex;
  justify-content: center;
}

// No results state in type list
.noResultsState {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: map.get($spacing-scale, 'lg');
}

// Right Content Area
.rightContent {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.rightContentHeader {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10; // Higher than historyTableHeader to allow popover to appear above table
  background: var(--color-surface-primary);
}

.emptySelection {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

// History Section
.historySection {
  padding: map.get($spacing-scale, 'lg');
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'md');
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.historySectionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: map.get($spacing-scale, 'xs');
}

.historySectionTitle {
  @include body-large-medium;
  color: var(--color-text-primary);
  margin: 0;
}

// History Table
.historyTable {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  overflow: hidden;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.historyTableHeader {
  display: flex;
  background: var(--color-surface-alt);
  border-bottom: 1px solid var(--color-border);
  padding: map.get($spacing-scale, 'sm') map.get($spacing-scale, 'md');
  position: sticky;
  top: 0;
  z-index: 1;
}

.historyTableBody {
  display: flex;
  flex-direction: column;
}

.historyTableRow {
  display: flex;
  border-bottom: 1px solid var(--color-border);
  padding: map.get($spacing-scale, 'md');
  cursor: pointer;

  @include transition(background-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--color-surface-alt);
  }
}

.historyTableCell {
  display: flex;
  align-items: center;
  @include body-small-regular;
  color: var(--color-text-primary);
}

.historyTableCellId {
  width: 80px;
  flex-shrink: 0;
}

.historyTableCellName {
  width: 140px;
  flex-shrink: 0;
}

.historyTableCellType {
  width: 100px;
  flex-shrink: 0;
}

.historyTableCellRequest {
  width: 100px;
  flex-shrink: 0;
  justify-content: center;
}

.historyTableCellOutput {
  width: 100px;
  flex-shrink: 0;
  justify-content: center;
}

.historyTableCellCreated {
  flex: 1;
  min-width: 180px;
}

// Empty History State
.emptyHistory {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px map.get($spacing-scale, 'lg');
}

// Responsive adjustments
@media (max-width: $breakpoint-md) {
  .main {
    padding: 0 map.get($spacing-scale, 'md');
  }

  .wrapper {
    padding: map.get($spacing-scale, 'md') 0;
  }

  .content {
    flex-direction: column;
  }

  .typeList {
    width: 100%;
    max-height: 400px;
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }

  .historyTable {
    overflow-x: auto;
  }

  .historyTableRow,
  .historyTableHeader {
    min-width: 800px;
  }
}

// Delete Confirmation Modal
.deleteBackdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 20px;

  [data-theme='dark'] & {
    background-color: rgba(0, 0, 0, 0.7);
  }
}

.deleteModalContent {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-3');
  padding: map.get($spacing-scale, 'lg');
}

.deleteModalText {
  @include body-regular;
  color: var(--color-text-primary);
  margin: 0;
}

.deleteModalTypeName {
  color: var(--color-semantic-danger);
}

.deleteModalWarning {
  @include body-small-regular;
  color: var(--color-text-secondary);
  margin: 0;
}

.addMenuWrapper {
  position: relative;
}

.addMenu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: map.get($spacing-scale, 'xs');
  min-width: 180px;
  background-color: var(--color-surface-primary);
  border: 1px solid var(--color-border);
  border-radius: map.get($radius, 'md');
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  // LeadTypeViewHeader elements use z-index 100 for their own popovers;
  // sit above them so this menu overlays the selected-type panel below.
  z-index: 1000;
  overflow: hidden;
}

.addMenuItem {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-3');
  width: 100%;
  padding: map.get($spacing-scale, 's-2') map.get($spacing-scale, 'md');
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-primary);
  white-space: nowrap;
  text-align: left;

  @include body-small-medium;
  @include transition(background-color);

  &:hover {
    background-color: var(--color-grey-light);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-base);
    outline-offset: -2px;
  }
}

.addMenuApolloIcon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  img {
    width: 18px;
    height: 18px;
  }
}
